import { supabase } from '@/core/database/supabase';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';

export interface IntegrationConfig {
  id?: string;
  company_id: string;
  service_key: string;
  api_key: string | null;
  is_active: boolean;
  config: any;
}

export const integrationsService = {
  async getIntegrations(companyId: string) {
    await enforceSettingsPermission('settings.integrations.view', { companyId });
    
    // Fetch real config from DB
    const { data: configs, error } = await supabase
      .from('external_integrations')
      .select('*')
      .eq('company_id', companyId);

    if (error) {
      console.error('[IntegrationsService] Error fetching configs:', error);
    }

    const configMap = (configs || []).reduce((acc: any, curr: any) => {
      acc[curr.service_key] = curr;
      return acc;
    }, {});

    // Merge with static metadata
    return [
      { 
        id: 'gemini', 
        name: 'Google Gemini AI', 
        provider: 'Google Cloud',
        description: 'Xử lý ngôn ngữ tự nhiên và phân tích dữ liệu cảnh quan thông minh.', 
        icon: 'bot', 
        status: configMap['gemini']?.api_key ? 'connected' : 'disconnected', 
        category: 'ai',
        color: 'from-blue-600 to-indigo-600',
        config: configMap['gemini'] || null,
        models: [
          { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', description: 'Mô hình siêu tốc độ mới nhất cho mọi tác vụ thời gian thực.' },
          { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', description: 'Mô hình ổn định và cân bằng tốt giữa tốc độ - chất lượng.' },
          { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', description: 'Mô hình thông minh nhất dành cho các bài toán phân tích phức tạp.' },
          { id: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash Lite', description: 'Phiên bản siêu nhẹ, tối ưu hóa chi phí và tốc độ phản hồi.' }
        ]
      },
      { 
        id: 'claude', 
        name: 'Claude AI', 
        provider: 'Anthropic',
        description: 'Mô hình ngôn ngữ tiên tiến với khả năng lập luận và viết lách tinh tế.', 
        icon: 'bot', 
        status: configMap['claude']?.api_key ? 'connected' : 'disconnected', 
        category: 'ai',
        color: 'from-purple-600 to-indigo-700',
        config: configMap['claude'] || null,
        models: ['claude-3-5-sonnet', 'claude-3-opus', 'claude-3-haiku']
      },
      { 
        id: 'chatgpt', 
        name: 'ChatGPT AI', 
        provider: 'OpenAI',
        description: 'Hệ thống AI phổ biến nhất thế giới cho các tác vụ đa năng.', 
        icon: 'bot', 
        status: configMap['chatgpt']?.api_key ? 'connected' : 'disconnected', 
        category: 'ai',
        color: 'from-teal-500 to-emerald-600',
        config: configMap['chatgpt'] || null,
        models: ['gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo']
      },
      { 
        id: 'vision-ai', 
        name: 'Landscape Vision', 
        provider: 'Custom AI',
        description: 'Nhận diện loài cây, sâu bệnh và đánh giá sức khỏe thực vật qua hình ảnh.', 
        icon: 'cpu', 
        status: configMap['vision-ai']?.api_key ? 'connected' : 'disconnected', 
        category: 'ai',
        color: 'from-emerald-600 to-teal-600',
        config: configMap['vision-ai'] || null,
        models: ['vision-v1-standard', 'vision-v1-high-res']
      },
      { 
        id: 'mapbox', 
        name: 'Mapbox Spatial', 
        provider: 'Mapbox',
        description: 'Bản đồ số độ phân giải cao và định vị vị trí các hạng mục thi công.', 
        icon: 'map', 
        status: configMap['mapbox']?.api_key ? 'connected' : 'disconnected', 
        category: 'maps',
        color: 'from-slate-700 to-slate-900',
        config: configMap['mapbox'] || null
      },
      { 
        id: 'weather', 
        name: 'OpenWeather', 
        provider: 'OpenWeatherMap',
        description: 'Dự báo thời tiết và cảnh báo rủi ro thiên tai cho dự án.', 
        icon: 'cloud-sun', 
        status: configMap['weather']?.api_key ? 'connected' : 'disconnected', 
        category: 'weather',
        color: 'from-amber-500 to-orange-600',
        config: configMap['weather'] || null
      },
    ];
  },

  async saveConfig(companyId: string, serviceKey: string, apiKey: string, selectedModel?: string) {
    await enforceSettingsPermission('settings.integrations.update', { companyId });

    const { data, error } = await supabase
      .from('external_integrations')
      .upsert({
        company_id: companyId,
        service_key: serviceKey,
        api_key: apiKey,
        config: { selectedModel },
        updated_at: new Date().toISOString()
      }, { onConflict: 'company_id,service_key' });

    if (error) throw error;
    return data;
  },

  async getLiveAI(companyId: string) {
    const { data } = await supabase
      .from('external_integrations')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .not('api_key', 'is', null)
      .limit(1);
    
    return data?.[0] || null;
  },

  async testConnection(serviceKey: string, apiKey: string) {
    // Giả lập việc ping API của nhà cung cấp
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simple logic: key length > 10 is "valid" for demo
        resolve(apiKey.length > 10);
      }, 1500);
    });
  }
};
